import gql from 'graphql-tag';

export { CREATE_MACHINE } from './createMachine';

const MACHINE_DETAIL_FRAGMENT = gql`
	fragment machineDetail on Machine {
		id
		assetId
		type
		brand
		place {
			id
			name
			destroyed
			directions
			visualDirectionPhotos {
				id
				link
				comment
			}
			visualDirectionPhotoUrls
		}

		machineKey {
			id
			name
		}

		manufacturer {
			id
			name
		}
		distributor {
			id
			name
		}
		machineModel {
			id
			name
		}
		machineType {
			id
			name
		}
		wrap {
			id
			name
		}
		aerialPosition {
			id
			name
		}
		hasCoinMech
		coinMech {
			id
			name
		}
		noteReader {
			id
			name
		}
		motherboard {
			id
			name
		}
		planogramCompliance {
			id
			name
		}

		complianceLabels {
			id
			name
		}

		route {
			id
			name
		}

		tags {
			id
			name
		}

		vendouts
		percentageFull
		salesSinceLastRestock
		daysSinceLastService
		lastCommunication
		salesLast7Days
		salesToday
		salesLast7DaysAth
		salesLast7DaysAthDate
		salesLast7DaysDailyAverage
		likelyServiceDays
		serviceDayAffinities {
			day
			ratio
			count
			opportunities
			lastPlanned
		}
		reportedPriceMismatchCount
		usesDynamicParlevels
		dynamicParlevelsSettings
		usesDynamicMinPick
		freshFoodOrderingEnabled
		freshFoodDeliveryDaysOfWeek
		freshFoodDefaultOrderAmount
		temperatureCelcius

		overrideRestrictedProductTags
		restrictedProductTags {
			id
		}
		hasCoinMech
		coinMech  {
			id
			name
			settings
		}

		overrideCoinLimits
		coinLimits

		machineModel {
			id
			name
		}
		accessHours
		location {
			id
			name
			accessHours
			directions
			requiresSignIn
			signInInstructions
			visualDirectionPhotos {
				id
				link
				comment
			}
			visualDirectionPhotoUrls
			restrictedProductTags {
				id
			}
		}
		previousLocation {
			id
			name
		}
		lastMoveDate

		planogram {
			id
			items {
				id
				price
				quantity
				parlevel
				enabled
				selection
				row
				column
				product {
					id
					name
					imageUrl
					HCGCategory
					HFDCategory
				}
				planogramItemStatistics {
					id
					spoilageLast30Days
				}
			}
		}
		receivedCommunicationTypes
		commDevices {
			id
			deviceId
			receivedCommunicationTypes
			manufacturer

			commDeviceLayout {
				id
				items {
					id
					price
					row
					column
					selection
					salesSinceInit
					vendsSinceInit
					productText
				}
				commDeviceIngredientLayout {
					id
					items {
						id
						row
						column
						selection
						salesSinceInit
						vendsSinceInit
					}
				}
			}
		}

		noteCount
		notes {
			id
			description
			created
			isAdminNote
			isImportant
			isPlanogramNote
			creator {
				id
				firstname
				lastname
				destroyed
			}
		}
		assignedTenant {
			id
			name
			region
		}

		rootTenantId
		created
		destroyed
		inactiveConditionType
		isInactive
		inactiveConditionDate
		inactiveConditionTicket {
			id
			description
			status
		}
		inactiveNote
	}
`;

const MACHINE_DETAIL_MOBILE_FRAGMENT = gql`
	fragment machineDetailMobile on Machine {
		id
		assetId
		type
		brand

		place {
			id
			name
			destroyed
			directions
			visualDirectionPhotos {
				id
				link
				comment
			}
			visualDirectionPhotoUrls
		}

		serialNumber

		daysSinceLastService
		lastCommunication

		coinMech  {
			id
			name
			settings
		}
		machineKey {
			id
			name
		}
		manufacturer  {
			id
			name
		}
		distributor {
			id
			name
		}
		machineModel {
			id
			name
		}
		machineType {
			id
			name
		}
		wrap {
			id
			name
		}
		aerialPosition {
			id
			name
		}
		location {
			id
			name
			addressObj {
				id
				region
				street
				city
				postcode
			}
			accessHours
			directions
			requiresSignIn
			signInInstructions
			visualDirectionPhotos {
				id
				link
				comment
			}
			visualDirectionPhotoUrls
			coordinates
		}
		noteReader {
			id
			name
		}
		motherboard {
			id
			name
		}
		accessHours
		directions
		coordinates
		useLocationCoordinates
		commDevices {
			id
			deviceId
		}
		route {
			id
			name
		}
		rootTenantId
		destroyed
	}
`;

export const GET_MACHINE_DETAIL_MOBILE = gql`
	query($id: String) {
		getMachine(id: $id) {
			...machineDetailMobile
		}
	}
	${MACHINE_DETAIL_MOBILE_FRAGMENT}
`;

export const SUBSCRIBE_TO_MACHINE_MOBILE = gql`
	subscription ($input: MachineSubscriptionInput!) {
		MachineSubscription(input: $input) {
			machine {
				...machineDetailMobile
			}
			destroyedMachineId
		}
	}
	${MACHINE_DETAIL_MOBILE_FRAGMENT}
`;

export const GET_MACHINE_DETAIL = gql`
	query($id: String, $includeDestroyed: Boolean) {
		getMachine(id: $id, includeDestroyed: $includeDestroyed) {
			...machineDetail
		}
	}
	${MACHINE_DETAIL_FRAGMENT}
`;

export const MODIFY_MACHINE = gql`
	mutation modifyMachineMutation($input: ModifyMachineInput!) {
		modifyMachine(input: $input) {
			machine {
				...machineDetail
			}
		}
	}
	${MACHINE_DETAIL_FRAGMENT}
`;

export const SUBSCRIBE_TO_MACHINE = gql`
	subscription ($input: MachineSubscriptionInput!) {
		MachineSubscription(input: $input) {
			machine {
				...machineDetail
			}
			destroyedMachineId
		}
	}
	${MACHINE_DETAIL_FRAGMENT}
`;

export const DESTROY_MACHINE = gql`
	mutation destroyMachineMutation ($id: ID!) {
		destroyMachine(id: $id)
	}
`;

export const GET_MACHINE_NOTES = gql`
	query($id: String) {
		getMachine(id: $id) {
			id
			notes {
				id
				description
				created
				isAdminNote
				isPlanogramNote
				isImportant
				creator {
					id
					firstname
					lastname
					destroyed
				}
			}
		}
	}
`;

export const GET_MACHINE_DYNAMIC_PARLEVELS_DETAIL = gql`
	query($id: String) {
		getMachine(id: $id) {
			id
			type
			created
			usesDynamicParlevels
			dynamicParlevelsSettings
			usesDynamicMinPick
			freshFoodOrderingEnabled
			freshFoodDeliveryDaysOfWeek
			freshFoodDefaultOrderAmount
		}
	}
`;

export const SET_DYNAMIC_PARLEVELS_SETTINGS = gql`
	mutation setDynamicParlevelsSettings ($machineId: ID!, $usesDynamicParlevels: Boolean, $settings: JSON!) {
		setDynamicParlevelsSettings (machineId: $machineId, settings: $settings, usesDynamicParlevels: $usesDynamicParlevels) {
			...machineDetail
		}
	}
	${MACHINE_DETAIL_FRAGMENT}
`;

export const GET_MACHINE_VENDOUT_PREDICTIONS = gql`
	query($id: String) {
		getMachine(id: $id) {
			id
			vendoutPredictions
		}
	}
`;

export const GET_MACHINE_MISSED_SALE_PREDICTIONS = gql`
	query($id: String) {
		getMachine(id: $id) {
			id
			missedSalePredictions
		}
	}
`;

export const GET_MACHINE_TAGS = gql`
	query($id: String) {
		getMachine(id: $id) {
			id
			tags {
				id
				name
			}
		}
	}
`;

export const UPDATE_MACHINE_TAGS_MUTATION = gql`
	mutation modifyMachineMutation($input: ModifyMachineInput!) {
		modifyMachine(input: $input) {
			machine {
				id
				tags {
					id
					name
				}
			}
		}
	}
`;

const GET_MACHINES_FOR_SERVICE_RUN_CREATOR_BASE_FRAGMENT = gql`
	fragment machineForServiceRunCreatorBase on Machine {
		id
		place {
			id
			name
			serviceGroup {
				id
				name
			}
		}
		type
		assetId
		brand
		location {
			id
			name
			noteCount
			isWarehouse
			addressObj {
				id
				region
				street
				city
				postcode
			}
			coordinates
			client {
				id
				name
			}
		}

		machineModel {
			id
			name
		}

		isInactive
		coordinates
		useLocationCoordinates
		vendouts
		vendoutPredictionTomorrow
		missedSalePredictionTomorrow
		missedSalePredictions
		averageMarginLast30Days
		percentageFull
		daysSinceLastService
		lastCommunication
		salesLast7Days
		salesToday
		salesLast7DaysAth
		salesLast7DaysAthDate
		salesLast7DaysDailyAverage
		likelyServiceDays
		serviceDayAffinities {
			day
			ratio
			count
			opportunities
			lastPlanned
		}
		reportedPriceMismatchCount
		lastSaleTime
		lastCashSaleTime
		lastCashlessSaleTime
		daysSinceLastCashSale
		daysSinceLastCashlessSale

		salesYesterday
		vendsSinceLastRestock
		salesSinceLastRestock
		coinsSinceLastCollection
		notesSinceLastCollection
		cashSinceLastCollection
		lastService
		averageDaysSinceLastService
		daysSinceLastService

		planogramId
		ingredientPlanogramId

		commDevices {
			id
			deviceId
			secondaryId
		}
		openTicketCount
		openIssueTicketCount
		openMaintenanceTicketCount
		openJobTicketCount
		noteCount
		isPlanned

		lastPhoto {
			id
			created
		}
		overrideMachinePhotoSchedule
		requireMachinePhotos
		machinesPhotosIntervalUnit
		machinesPhotosIntervalUnits
		shouldPreselectMachineWhenPlanningRun
		planogramItemNextExpiryDate
		permanentPackTags {
			id
			name
			abbreviation
		}

	requiredSitePermissions {
		id
		name
		durationInDays
		daysOfWeek
		policy
		clients {
			id
		}
		locations {
			id
		}
		places {
			id
		}
		legacyMachines {
			id
		}
	}
	siteBookingNotices {
		id
		name
		bookingProcess
		clients {
			id
		}
		locations {
			id
		}
		places {
			id
		}
	}

		totalPickItemsToBePicked

		destroyed
		machineSetup {
			id
		}
		marketSetup {
			id
		}
	}
`;

const GET_MACHINES_FOR_SERVICE_RUN_CREATOR_DOCKETS_FRAGMENT = gql`
	fragment machineForServiceRunCreatorDockets on Machine {
		serviceDockets {
			id
			planned
			serviceRun {
				id
				driver {
					id
					firstname
					lastname
					destroyed
				}
			}
		}
	}
`;

const GET_MACHINES_FOR_SERVICE_RUN_CREATOR_FRAGMENT = gql`
	fragment machineForServiceRunCreator on Machine {
		...machineForServiceRunCreatorBase
		...machineForServiceRunCreatorDockets
		freshFoodManufacturers {
			id
			name
		}
	}
	${GET_MACHINES_FOR_SERVICE_RUN_CREATOR_BASE_FRAGMENT}
	${GET_MACHINES_FOR_SERVICE_RUN_CREATOR_DOCKETS_FRAGMENT}
`;

export const GET_MACHINES_FOR_SERVICE_RUN_CREATOR = gql`
	query(
		$page: Int,
		$perPage: Int,
		$sortBy: String!,
		$sortDirection: Int!,
		$filter: MachinePageFilter
	) {
		getMachines(
			page: $page,
			perPage: $perPage,
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				...machineForServiceRunCreator
			}
			count
		}
	}
	${GET_MACHINES_FOR_SERVICE_RUN_CREATOR_FRAGMENT}
`;

export const GET_MACHINES_FOR_SERVICE_RUN_PLANNER = gql`
	query(
		$page: Int,
		$perPage: Int,
		$sortBy: String!,
		$sortDirection: Int!,
		$filter: MachinePageFilter
	) {
		getMachines(
			page: $page,
			perPage: $perPage,
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				...machineForServiceRunCreator
				openTickets {
					id
					type
					priority
					visibleToDrivers
				}
			}
			count
		}
	}
	${GET_MACHINES_FOR_SERVICE_RUN_CREATOR_FRAGMENT}
`;

export const GET_MACHINES_FOR_SERVICE_RUN_PLANNER_SUBSCRIPTION = gql`
	subscription ($input: MachineSubscriptionInput!) {
		MachineSubscription(input: $input) {
			machine {
				...machineForServiceRunCreatorBase
				openTickets {
					id
					type
					priority
					visibleToDrivers
				}
			}
		}
	}
	${GET_MACHINES_FOR_SERVICE_RUN_CREATOR_BASE_FRAGMENT}
`;

export const ASSIGN_MACHINE_TO_TENANT_MUTATION = gql`
	mutation assignMachineToTenant($machineId: ID, $tenantId: ID) {
		assignMachineToTenant(machineId: $machineId, tenantId: $tenantId) {
			id
			assignedTenant {
				id
				name
			}
		}
	}
`;

export const GET_MACHINES_EXPIRY_PRODUCTS_COUNT = gql`
	query getMachinesExpiryProductsCount(
		$machineIds: [ID],
		$date: Date,
		$routeIds: [ID],
		$defaultMachineRestockType: Int,
		$machineRestockTypes: JSON,
	) {
		getMachinesExpiryProductsCount(
			machineIds: $machineIds,
			date: $date,
			routeIds: $routeIds,
			defaultMachineRestockType: $defaultMachineRestockType,
			machineRestockTypes: $machineRestockTypes,
		)
	}
`;

export const GET_MACHINE_PERCENTAGE_FULL_BREAKDOWN = gql`
	query($id: String) {
		getMachine(id: $id) {
			id
			percentageFull
			stockPercentagesByFreshFood {
				freshFoodPercentageFull
				otherProductsPercentageFull
			}
		}
	}
`;
